import './branch-details.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import { BreadcrumbItem } from '@celebration/react/ClbBreadcrumb/ClbBreadcrumb.types';
import Subheader from '../../common/components/subheader';
import BranchService from '../../common/services/branch/branch.service';
import Overview from '../../common/components/branches/overview';
import BranchesConstants from '../branches/constants';
import CarriersConstants from '../carriers/constants';
import HomeConstants from '../home/constants';
import IBranchInfo from './interfaces/IBranchInfo';
import IBranchFormData from '../../common/interfaces/IBranchFormData';
import { IRegionLocations } from '../../common/interfaces/IRegionLocations';
import { ILocation } from '../../common/interfaces/ILocation';
import BranchesDetailsConstants from './constants';
import { ICarriersContact } from '../../common/interfaces/IContacts';
import BranchFormService from '../branches/components/branch-form/services/branch-form.service';
import { useToast } from '../../app/contexts/toast/toast.context';

function BranchDetails() {
    const { t } = useTranslation();
    const { state } = useLocation();
    const { setToast } = useToast();

    const branchDetailData = useRef<IBranchFormData>({} as IBranchFormData);
    const branchRegionData = useRef<IRegionLocations[]>(
        [] as IRegionLocations[],
    );
    const carrierContactData = useRef<ICarriersContact>({} as ICarriersContact);

    const [showLoading, setShowLoading] = useState<boolean>(true);

    const breadcrumbList: BreadcrumbItem[] = [
        { label: t(HomeConstants.PAGE_TITLE), url: HomeConstants.PATH },
        { label: t(CarriersConstants.PAGE_TITLE), url: CarriersConstants.PATH },
        { label: t(BranchesConstants.PAGE_TITLE), url: BranchesConstants.PATH },
        {
            label: t(BranchesDetailsConstants.PAGE_TITLE),
            url: BranchesDetailsConstants.PATH,
        },
    ];

    const pageTitle = useMemo(() => {
        let title = t(BranchesConstants.PAGE_TITLE);

        const branchInfo = state as IBranchInfo;
        if (branchInfo) {
            title = `${branchInfo.carrierName} - ${branchInfo.branchName}`;
        }

        return title;
    }, [state, t]);

    const getBranchDetailData = useCallback(async () => {
        const branchInfo = state as IBranchInfo;
        branchDetailData.current = await BranchService.getBranch(
            branchInfo.carrierId,
            branchInfo.branchId,
            t,
        );
    }, [state, t]);

    const getBranchRegionData = useCallback(async () => {
        const branchInfo = state as IBranchInfo;
        const branchRegions = await BranchService.getBranchLocations(
            branchInfo.carrierId,
            branchInfo.branchId,
            t,
        );

        const branchRegionsOverview = branchRegions.map(currentBranchRegion => {
            return {
                abbreviation: currentBranchRegion.abbreviation,
                name: currentBranchRegion.name,
                locations: currentBranchRegion.locations.map(
                    currentLocation => {
                        return {
                            code: currentLocation.code,
                            name: currentLocation.name,
                            sapCode: currentLocation.sapCode,
                            id: currentLocation.id,
                        } as ILocation;
                    },
                ),
            } as IRegionLocations;
        });
        branchRegionData.current = branchRegionsOverview;
    }, [state, t]);

    const getCarrierContactData = useCallback(async () => {
        const branchInfo = state as IBranchInfo;
        const carrierContact = await BranchFormService.getCarriersInfo(
            branchInfo.carrierId,
        );

        carrierContactData.current = carrierContact.carrierContact;
    }, [state]);

    useEffect(() => {
        const branchDetailCall = getBranchDetailData();
        const branchRegionCall = getBranchRegionData();
        const carrierContactCall = getCarrierContactData();

        const fetchPromises = [
            branchDetailCall,
            branchRegionCall,
            carrierContactCall,
        ];
        Promise.all(fetchPromises)
            .catch((error: Error) => {
                setToast('negative', t('Erro'), error.message);
            })
            .finally(() => setShowLoading(false));
    }, [
        getBranchDetailData,
        getBranchRegionData,
        getCarrierContactData,
        state,
        setToast,
        t,
    ]);

    return (
        <>
            <Subheader
                title={pageTitle}
                breadcrumbList={breadcrumbList}
                dataTestid="branches-subheader"
            />

            <div className="container-branch-details">
                {showLoading && <ClbLoading />}
                {!showLoading && (
                    <Overview
                        branchData={branchDetailData.current}
                        regionData={branchRegionData.current}
                        carrierContactData={carrierContactData.current}
                    />
                )}
            </div>
        </>
    );
}

export default BranchDetails;
